// React import
import { useState, useMemo, useEffect, useRef, Fragment } from 'react';

// MUI import
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { DataGrid } from '@mui/x-data-grid';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';

// FontAwesome import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { faFileJpg, faFilePdf, faFileWord, faFileExcel, faFilePowerpoint, faFile, faTriangleExclamation, faFileCsv, faFileCode } from '@fortawesome/pro-light-svg-icons';

// Generic import
import _ from 'lodash';

// Local import
import { createXmlStructureFromOpportunity, parseStructureToXMLString, parsePastedAmount } from './CWXml.utils.js';
import Where from './Where.js';
import CurrencyTextField from './dashboard/components/CurrencyTextField';
import CURRENCIES_NAME from './CWXml.currencies.js';
import currencyToText from './CurrencyToText.js';
import Autocomplete from './AutocompleteWithReactWindow.js';
import readableId from './ReadableId.js';
import { chargeLinesToCsv, downloadTextFile } from './CWXml.download.js';

// Columns that take a paste on a highlighted cell (Cmd/Ctrl+V without opening it).
const PASTE_FIELDS = [ 'Description', 'ChargeCode-Code', 'SellOSAmountCurrency', 'CostOSAmountCurrency', 'CreditorName', 'DebtorName' ];

// One heading style for every block in the dialog. Previously each section rolled its own
// (some with marginTop 24, some none, some margin '12px 0px'), which read as uneven spacing.
function SectionTitle({ children, sx = {} }) {
    return (
        <Typography variant='caption' component={Box} sx={{
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '11px',
            color: 'text.secondary',
            marginTop: '20px',
            marginBottom: '6px',
            ...sx,
        }}>
            {children}
        </Typography>
    );
}

export default function CWXml(props) {
    const { initialStructure, organization, opportunity, me, account, accounts, suppliers, open, onClose, onOk, seaPorts, exchangeRates, unlocode, lockedExchangeRates } = props;
    const { transportationMethod, products } = opportunity;

    const [ structure, setStructure ] = useState(null);
    const [ branch, setBranch ] = useState(':');
    const [ selectedCompanyIndex, setSelectedCompanyIndex ] = useState(0);
    // 0 = review and edit, 1 = download and send. Downloads sit in their own step so they are
    // taken after every edit to the table, and match what is sent.
    const [ step, setStep ] = useState(0);

    useEffect(() => {
        if (open) setStep(0);
    }, [ open ]);

    const containerModeIndex = transportationMethod === 'sea' ? (products.find(p => p.type === 'By Dimensions or Weight (sea)' || p.type === 'By Volume or Weight (sea)' || p.type === 'LCL') ? 1 : 0) : 0;

    function _onOk() {
        onOk({ structure, text: parseStructureToXMLString(structure) });
    }

    const downloadName = extension => `cargowise-${readableId(opportunity)}.${extension}`;

    function onDownloadCsv() {
        downloadTextFile({ text: chargeLinesToCsv(rows), filename: downloadName('csv'), type: 'text/csv' });
    }

    // The same XML the server sends (it serializes this structure unchanged), indented to read.
    function onDownloadXml() {
        downloadTextFile({ text: parseStructureToXMLString(structure), filename: downloadName('xml'), type: 'application/xml' });
    }

    // Get companies array with backward compatibility
    const companies = useMemo(() => {
        let companiesList = _.get(organization, 'integrations.cargowise.companies', []);
        if (companiesList.length === 0 && (_.get(organization, 'integrations.cargowise.companyCode') || _.get(organization, 'integrations.cargowise.enterpriseId') || _.get(organization, 'integrations.cargowise.serverId'))) {
            // Migrate legacy single company to companies array
            companiesList = [{
                companyCode: _.get(organization, 'integrations.cargowise.companyCode', ''),
                enterpriseId: _.get(organization, 'integrations.cargowise.enterpriseId', ''),
                serverId: _.get(organization, 'integrations.cargowise.serverId', ''),
                edocsemail: _.get(organization, 'integrations.cargowise.edocsemail', ''),
                branchDepartments: _.get(organization, 'integrations.cargowise.branchDepartments', [])
            }];
        }
        return companiesList;
    }, [organization]);

    // Initialize selectedCompanyIndex when companies are available
    useEffect(() => {
        if (companies.length > 0 && selectedCompanyIndex >= companies.length) {
            setSelectedCompanyIndex(0);
        }
    }, [companies, selectedCompanyIndex]);

    useEffect(() => {
        const dataTargetType = (organization.integrations.cargowise || {}).dataTargetType;
        const struct = initialStructure ? initialStructure : createXmlStructureFromOpportunity({
            organization,
            opportunity,
            me,
            account,
            suppliers,
            containerModeIndex,
            dataTargetType,
            exchangeRates,
            lockedExchangeRates,
        });

        const shipment = struct.children.find(child => child.name === 'Shipment');
        const dataContext = shipment.children.find(child => child.name === 'DataContext');
        const company = dataContext.children.find(child => child.name === 'Company');
        const companyCode = company.children.find(child => child.name === 'Code');
        
        // Use selected company's info, or fallback to legacy fields
        const selectedCompany = companies[selectedCompanyIndex] || {};
        companyCode.value = selectedCompany.companyCode || (organization.integrations.cargowise || {}).companyCode || '';
        const enterpriseId = dataContext.children.find(child => child.name === 'EnterpriseID');
        enterpriseId.value = selectedCompany.enterpriseId || (organization.integrations.cargowise || {}).enterpriseId || '';
        const serverId = dataContext.children.find(child => child.name === 'ServerID');
        serverId.value = selectedCompany.serverId || (organization.integrations.cargowise || {}).serverId || '';

        setStructure(struct);
    }, [ initialStructure, organization, opportunity, account, suppliers, containerModeIndex, me, selectedCompanyIndex, companies ]);

    function onReset() {
        const dataTargetType = (organization.integrations.cargowise || {}).dataTargetType;
        setStructure(createXmlStructureFromOpportunity({
            organization,
            opportunity,
            me,
            account,
            suppliers,
            containerModeIndex,
            dataTargetType,
            exchangeRates,
            lockedExchangeRates,
        }));
    }

    const dirty = useMemo(() => {
        const dataTargetType = (organization.integrations.cargowise || {}).dataTargetType;
        return !_.isEqual(
            structure,
            createXmlStructureFromOpportunity({
                organization,
                opportunity,
                me,
                account,
                suppliers,
                containerModeIndex,
                dataTargetType,
                exchangeRates,
                lockedExchangeRates,
            }));
    }, [ structure, organization, opportunity, account, suppliers, containerModeIndex, me, exchangeRates ]);

    // The CargoWise code is the last ':'-segment of uniqueId. Records saved with an
    // empty code (e.g. uniqueId '::' from `${branchCode}::${code}` in utils/account.js)
    // collapse to '' — and '' is also what a supplier with no uniqueId at all produces
    // as a Creditor-Key. Left in, those records become a catch-all that silently matches
    // every code-less supplier. Keep only entries with a real code.
    const toCreditor = s => {
        const code = _.last((s.uniqueId || '').split(':'));
        return code ? { name: `${s.name} (${code})`, uniqueId: code } : null;
    };
    const creditors = useMemo(() => {
        if (!suppliers) return [];
        const s = _.compact(suppliers.filter(s => s.uniqueId && !s.inactive).map(toCreditor));
        const a = _.compact(accounts.filter(s => s.uniqueId && !s.inactive).map(toCreditor));
        return _.sortBy(_.uniqBy([ ...s, ...a, { name: 'Monada (MONADATLV)', uniqueId: 'MONADATLV' }], 'uniqueId'), 'name');
    }, [suppliers, accounts]);

    const chargeLinesOf = struct => struct.children.find(child => child.name === 'Shipment')
        .children.find(child => child.name === 'JobCosting')
        .children.find(child => child.name === 'ChargeLineCollection')
        .children;

    // Changes the charge line behind one table row (its id is the line's position).
    function updateChargeLine(rowId, change) {
        setStructure(_struct => {
            const struct = JSON.parse(JSON.stringify(_struct));
            const chargeLine = chargeLinesOf(struct)[rowId];
            if (chargeLine) change(chargeLine);
            return struct;
        });
    }

    const rows = useMemo(() => {
        if (!structure) return [];
        const shipment = structure.children.find(child => child.name === 'Shipment');
        const jobCosting = shipment.children.find(child => child.name === 'JobCosting');
        const chargeLineCollection = jobCosting.children.find(child => child.name === 'ChargeLineCollection');
        return chargeLineCollection.children.map((child, index) => {
            const ret = child.children.reduce((acc, child) => {
                (child.children || []).forEach(grandChild => {
                    acc[`${child.name}-${grandChild.name}`] = grandChild.value;
                });
                acc[child.name] = child.value;
                // Display-only metadata hung off the node (see createXmlStructureFromOpportunity).
                if (child.supplierName) acc[`${child.name}-SupplierName`] = child.supplierName;
                return acc;
            }, {});
            // Rows are keyed by their position in the charge lines. The old key (description + sell
            // amount) repeated when a quote has the same charge twice, and changed while it was
            // being edited; either way the table crashed ("No row with id … found").
            ret.id = index;
            // An empty key means "no CargoWise code", never "match whatever has no code".
            const findCreditor = key => (key ? creditors.find(s => s.uniqueId === key) : null) || {};
            ret.DebtorName = findCreditor(ret['Debtor-Key']).name || ret['Debtor-Key'];
            ret.CreditorName = findCreditor(ret['Creditor-Key']).name || ret['Creditor-Key'];
            ret.SellOSAmountCurrency = `${ret.SellOSAmount}|${ret['SellOSCurrency-Code']}`;
            ret.CostOSAmountCurrency = `${ret.CostOSAmount}|${ret['CostOSCurrency-Code']}`;
            return ret;
        });
    }, [structure, creditors]);

    const errors = useMemo(() => {
        if (!structure) return [];
        const errors = rows.map(row => {
            const errors = [];
            if (!row.DebtorName) errors.push(`Debtor is missing from ${row.Description}`);
            if (row.DebtorName && !creditors.find(s => s.uniqueId === row['Debtor-Key'])) errors.push(`Debtor ${row.DebtorName} from ${row.Description} is not found in the system`);
            if (!row.CreditorName) errors.push(
                row['Creditor-SupplierName']
                    ? `${row['Creditor-SupplierName']} has no CargoWise code — set one on the supplier, or pick a creditor for ${row.Description}`
                    : `Creditor is missing from ${row.Description}`
            );
            if (row.CreditorName && !creditors.find(s => s.uniqueId === row['Creditor-Key'])) errors.push(`Debtor ${row.CreditorName} from ${row.Description} is not found in the system`);
            if (!row['ChargeCode-Code'] || (row['ChargeCode-Code'] === '-NA-')) errors.push(`Charge Code is missing from ${row.Description}`);
            return errors;
        }).flat();

        const source = structure?.children[0].children.find(child => child.name === 'PortOfOrigin')?.children.find(child => child.name === 'Code')?.value;
        const destination = structure?.children[0].children.find(child => child.name === 'PortOfDestination')?.children.find(child => child.name === 'Code')?.value;
        if (!source) errors.push('Port of Origin is missing');
        if (!destination) errors.push('Port of Destination is missing');
        if (companies.length === 0) errors.push('Please select a company');
        if (!branch || !branch.split(':')[0] || !branch.split(':')[1] || !branch.split(':')[2] || !branch.split(':')[3]) errors.push('Please select a branch and department');
        return errors;
    }, [rows, structure, branch, companies ]);

    // Things worth knowing that don't stop the send: a dangerous product whose class or UN code
    // isn't known yet still goes to CargoWise, but without a real class or code there.
    const warnings = useMemo(() => (products || [])
        .filter(p => p.dangerous && (!p.dangerousClass || !p.dangerousUNCode))
        .map(p => {
            const missing = [!p.dangerousClass && 'class', !p.dangerousUNCode && 'UN code'].filter(Boolean).join(' or ');
            return `${p.quantity || 1} x ${p.type} is marked dangerous but has no ${missing}. Add it on the quote's product before sending, or CargoWise won't have the right details.`;
        }), [products]);

    // Get branchDepartments for selected company
    const branchDepartments = useMemo(() => {
        const selectedCompany = companies[selectedCompanyIndex];
        if (selectedCompany && selectedCompany.branchDepartments) {
            return selectedCompany.branchDepartments;
        }
        // Fallback to legacy branchDepartments
        return _.get(organization, 'integrations.cargowise.branchDepartments', []);
    }, [companies, selectedCompanyIndex, organization]);

    useEffect(() => {
        if (rows.length > 0) {
            setBranch(`${rows[0]['Branch-Name']}:${rows[0]['Branch-Code']}:${rows[0]['Department-Name']}:${rows[0]['Department-Code']}`);
        } else if (branchDepartments.length > 0) {
            const firstBranch = branchDepartments[0];
            setBranch(`${firstBranch.branchName}:${firstBranch.branchCode}:${firstBranch.departmentName}:${firstBranch.departmentCode}`);
        } else {
            setBranch(':');
        }
    }, [rows, branchDepartments]);

    const summary = (() => {
        const shipment = structure?.children[0];
        const port = name => {
            const node = shipment?.children.find(child => child.name === name);
            const code = node?.children.find(child => child.name === 'Code')?.value;
            const text = node?.children.find(child => child.name === 'Name')?.value;
            return text || code || 'Missing';
        };
        const company = companies[selectedCompanyIndex];
        const [ branchName, branchCode, departmentName, departmentCode ] = (branch || '').split(':');
        return _.compact([
            company ? [ 'Company', `${company.companyCode || ''} (${company.enterpriseId || ''})` ] : null,
            [ 'Branch/Department', branchCode ? `${branchName} (${branchCode}) - ${departmentName} (${departmentCode})` : 'Missing' ],
            [ 'Route', `${port('PortOfOrigin')} → ${port('PortOfDestination')}` ],
            [ 'Charge lines', rows.length ],
        ]);
    })();

    // A creditor/debtor typed or pasted as text: its name as listed, or its CargoWise code.
    const findCreditorByText = text => {
        const t = (text || '').trim().toUpperCase();
        if (!t) return null;
        return creditors.find(s => s.name.toUpperCase() === t || (s.uniqueId || '').toUpperCase() === t) || null;
    };

    // Writes an edited row back into the charge-line structure. The grid calls this whenever a cell
    // edit ends — Enter, Tab, clicking another cell or anywhere outside the table — so a typed or
    // pasted value is kept however the cell is left. Before, cells saved only on Enter or blur, and
    // the grid often closed the cell before its blur ran, dropping the value.
    function processRowUpdate(newRow, oldRow) {
        const changed = key => newRow[key] !== oldRow[key];
        const result = { ...newRow };
        [ 'SellOSAmountCurrency', 'CostOSAmountCurrency' ].forEach(key => {
            if (!changed(key)) return;
            const [ value, currency ] = newRow[key].split('|');
            result[key] = `${value === '' ? '0' : value}|${currency || oldRow[key].split('|')[1]}`;
        });
        const creditor = changed('CreditorName') ? findCreditorByText(newRow.CreditorName) : null;
        const debtor = changed('DebtorName') ? findCreditorByText(newRow.DebtorName) : null;
        // The grid shows the returned row, so it must read like the rows built from the structure:
        // the creditor's listed name, or the old value when the text matches no creditor.
        const creditorKey = creditor ? (creditor.uniqueId || creditor.name) : null;
        const debtorKey = debtor ? (_.last((debtor.uniqueId || '').split(':')) || debtor.name) : null;
        const nameForKey = key => (creditors.find(s => s.uniqueId === key) || {}).name || key;
        if (changed('CreditorName')) result.CreditorName = creditor ? nameForKey(creditorKey) : oldRow.CreditorName;
        if (changed('DebtorName')) result.DebtorName = debtor ? nameForKey(debtorKey) : oldRow.DebtorName;

        updateChargeLine(newRow.id, chargeLine => {
            const node = name => chargeLine.children.find(child => child.name === name);
            const sub = (name, childName) => node(name).children.find(child => child.name === childName);

            if (changed('Description')) node('Description').value = newRow.Description;
            if (changed('ChargeCode-Code')) {
                sub('ChargeCode', 'Code').value = newRow['ChargeCode-Code'];
                const matchingCriteria = sub('ImportMetaData', 'MatchingCriteriaCollection').children.find(child => child.name === 'MatchingCriteria');
                matchingCriteria.children.find(child => child.name === 'Value').value = newRow['ChargeCode-Code'];
            }
            [ [ 'SellOSAmountCurrency', 'SellOSAmount', 'SellOSCurrency' ], [ 'CostOSAmountCurrency', 'CostOSAmount', 'CostOSCurrency' ] ].forEach(([ key, amountName, currencyName ]) => {
                if (!changed(key)) return;
                // Delete (or typing over the cell) can hand back '' — keep the row's currency, read empty as 0.
                const [ value, typedCurrency ] = newRow[key].split('|');
                const currency = typedCurrency || oldRow[key].split('|')[1];
                node(amountName).value = value === '' ? '0' : value;
                sub(currencyName, 'Code').value = currency;
                sub(currencyName, 'Description').value = CURRENCIES_NAME[currency];
            });
            if (creditor) sub('Creditor', 'Key').value = creditorKey;
            if (debtor) sub('Debtor', 'Key').value = debtorKey;
        });
        return result;
    }

    const creditorOptions = _(creditors).map(s => s.name).uniq().sortBy(a => a.toUpperCase()).value();

    const columns = [
        {
            field: '',
            headerName: '',
            width: 50,
            type: 'actions',
            renderCell: (params) => (
                <IconButton variant='text' color='error' onClick={() => {
                    setStructure(_struct => {
                        const struct = JSON.parse(JSON.stringify(_struct));
                        const chargeLines = chargeLinesOf(struct);
                        chargeLines.splice(params.id, 1);
                        return struct;
                    });
                }}><FontAwesomeIcon icon={faTrash} /></IconButton>
            )
        },
        { field: 'Description', headerName: 'Field', width: 287, editable: true, renderEditCell: (params) => <TextEditCell params={params} rowCount={rows.length} /> },
        { field: 'ChargeCode-Code', headerName: 'Charge Code', width: 137, editable: true, renderEditCell: (params) => <TextEditCell params={params} rowCount={rows.length} /> },
        {
            field: 'SellOSAmountCurrency',
            headerName: 'Sell Amount',
            width: 100,
            editable: true,
            renderCell: (params) => {
                const [ val, currency ] = params.value.split('|');
                return currencyToText(currency, true, val);
            },
            renderEditCell: (params) => <CurrencyEditCell params={params} organization={organization} />,
        }, {
            field: 'CostOSAmountCurrency',
            headerName: 'Cost Amount',
            width: 100,
            editable: true,
            renderCell: (params) => {
                const [ val, currency ] = params.value.split('|');
                return currencyToText(currency, true, val);
            },
            renderEditCell: (params) => <CurrencyEditCell params={params} organization={organization} />,
        }, {
            field: 'CreditorName',
            headerName: 'Creditor',
            width: 287,
            editable: true,
            renderEditCell: (params) => <CreditorEditCell params={params} options={creditorOptions} />,
        }, {
            field: 'DebtorName',
            headerName: 'Debtor',
            width: 290,
            editable: true,
            renderEditCell: (params) => <CreditorEditCell params={params} options={creditorOptions} />,
        },
    ];

    return (
        <Dialog
            maxWidth={'lg'}
            fullWidth
            open={open}
            onClose={(event, reason) => {
                // Escape inside the charges table cancels the cell edit; it must not also close the
                // dialog and throw away every edit made in it.
                if (reason === 'escapeKeyDown' && event?.target?.closest?.('.MuiDataGrid-root')) return;
                onClose();
            }}
        >
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='h6'>Send to CargoWise</Typography>
                    {step === 0 && <Button variant='outlined' color='primary' disabled={!dirty} onClick={onReset}>Reset Data</Button>}
                </Box>
                <Typography variant='body2' sx={{ color: 'text.secondary', marginTop: '4px', fontSize: '13px' }}>
                    {step === 0
                        ? 'Review the details below before creating the shipment in CargoWise.'
                        : 'Download the charges table or the XML file if you need a copy, then send the shipment to CargoWise.'}
                </Typography>
                <Stepper activeStep={step} sx={{ marginTop: '16px', maxWidth: '420px' }}>
                    <Step><StepLabel>Review &amp; edit</StepLabel></Step>
                    <Step><StepLabel>Download &amp; send</StepLabel></Step>
                </Stepper>
                {step === 0 && (<>
                {companies.length > 0 && (
                    <>
                        <SectionTitle>Company</SectionTitle>
                        <Typography variant='body2' sx={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }} component={Box}>
                            <Select
                                fullWidth
                                value={selectedCompanyIndex}
                                onChange={(e) => {
                                    const newIndex = e.target.value;
                                    setSelectedCompanyIndex(newIndex);
                                    const selectedCompany = companies[newIndex];
                                    
                                    // Update structure with new company info
                                    setStructure(_struct => {
                                        const _s = JSON.parse(JSON.stringify(_struct));
                                        const shipment = _s.children.find(child => child.name === 'Shipment');
                                        const dataContext = shipment.children.find(child => child.name === 'DataContext');
                                        const company = dataContext.children.find(child => child.name === 'Company');
                                        const companyCode = company.children.find(child => child.name === 'Code');
                                        companyCode.value = selectedCompany?.companyCode || '';
                                        const enterpriseId = dataContext.children.find(child => child.name === 'EnterpriseID');
                                        enterpriseId.value = selectedCompany?.enterpriseId || '';
                                        const serverId = dataContext.children.find(child => child.name === 'ServerID');
                                        serverId.value = selectedCompany?.serverId || '';
                                        return _s;
                                    });
                                    
                                    // Reset branch selection when company changes
                                    const newBranchDepartments = selectedCompany?.branchDepartments || [];
                                    if (newBranchDepartments.length > 0) {
                                        const firstBranch = newBranchDepartments[0];
                                        const branchValue = `${firstBranch.branchName}:${firstBranch.branchCode}:${firstBranch.departmentName}:${firstBranch.departmentCode}`;
                                        setBranch(branchValue);
                                        
                                        // Update structure with branch/department info
                                        setStructure(_struct => {
                                            const _s = JSON.parse(JSON.stringify(_struct));
                                            const shipment = _s.children.find(child => child.name === 'Shipment');
                                            const _branchShipment = shipment.children.find(child => child.name === 'Branch');
                                            _branchShipment.children.find(child => child.name === 'Code').value = firstBranch.branchCode;

                                            const jobCosting = shipment.children.find(child => child.name === 'JobCosting');
                                            const chargeLineCollection = jobCosting.children.find(child => child.name === 'ChargeLineCollection');

                                            chargeLineCollection.children.forEach(_chargeLine => {
                                                const _branch = _chargeLine.children.find(child => child.name === 'Branch');
                                                _branch.children.find(child => child.name === 'Name').value = firstBranch.branchName;
                                                _branch.children.find(child => child.name === 'Code').value = firstBranch.branchCode;
                                            });

                                            chargeLineCollection.children.forEach(_chargeLine => {
                                                const _department = _chargeLine.children.find(child => child.name === 'Department');
                                                _department.children.find(child => child.name === 'Name').value = firstBranch.departmentName;
                                                _department.children.find(child => child.name === 'Code').value = firstBranch.departmentCode;
                                            });

                                            return _s;
                                        });
                                    } else {
                                        setBranch(':');
                                    }
                                }}
                                displayEmpty
                                renderValue={(value) => {
                                    if (value === '' || value === null || companies.length === 0) {
                                        return <Typography sx={{ color: '#999' }}>Choose company here</Typography>;
                                    }
                                    const company = companies[value];
                                    return `${company.companyCode || ''} (${company.enterpriseId || ''})`;
                                }}
                            >
                                {companies.map((company, index) => (
                                    <MenuItem key={index} value={index}>
                                        {company.companyCode || 'Unnamed'} ({company.enterpriseId || ''})
                                    </MenuItem>
                                ))}
                            </Select>
                        </Typography>
                    </>
                )}
                <SectionTitle>Branch/Department</SectionTitle>
                <Typography variant='body2' sx={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }} component={Box}>
                    <Select
                        fullWidth
                        value={branch}
                        onChange={(e) => {
                            setBranch(e.target.value);
                            const [ branchName, branchCode, departmentName, departmentCode ] = e.target.value.split(':');

                            setStructure(_struct => {
                                const _s = JSON.parse(JSON.stringify(_struct));
                                const shipment = _s.children.find(child => child.name === 'Shipment');
                                const _branchShipment = shipment.children.find(child => child.name === 'Branch');
                                _branchShipment.children.find(child => child.name === 'Code').value = branchCode;

                                const jobCosting = shipment.children.find(child => child.name === 'JobCosting');
                                const chargeLineCollection = jobCosting.children.find(child => child.name === 'ChargeLineCollection');

                                chargeLineCollection.children.forEach(_chargeLine => {
                                    const _branch = _chargeLine.children.find(child => child.name === 'Branch');
                                    _branch.children.find(child => child.name === 'Name').value = branchName;
                                    _branch.children.find(child => child.name === 'Code').value = branchCode;
                                });

                                chargeLineCollection.children.forEach(_chargeLine => {
                                    const _department = _chargeLine.children.find(child => child.name === 'Department');
                                    _department.children.find(child => child.name === 'Name').value = departmentName;
                                    _department.children.find(child => child.name === 'Code').value = departmentCode;
                                });

                                return _s;
                            })
                        }}
                        displayEmpty
                        renderValue={(value) => {
                            if (!value || value === ':' || branchDepartments.length === 0) {
                                return <Typography sx={{ color: '#999' }}>Choose Branch and department here</Typography>;
                            }
                            const [ branchName, branchCode, departmentName, departmentCode ] = value.split(':');
                            return `Branch: ${branchName} (${branchCode}) - Department: ${departmentName} (${departmentCode})`;
                        }}
                    >
                        {branchDepartments.map(e => (
                            <MenuItem key={`${e.branchCode}-${e.departmentCode}`} value={`${e.branchName}:${e.branchCode}:${e.departmentName}:${e.departmentCode}`}>Branch: {e.branchName} ({e.branchCode}) - Department: {e.departmentName} ({e.departmentCode})</MenuItem>
                        ))}
                    </Select>
                </Typography>
                <SectionTitle>Routes</SectionTitle>
                <Box sx={{ marginBottom: '12px' }}>
                    <Where
                        transportationMethod={transportationMethod}
                        errors={[]}
                        source={{
                            id: structure?.children[0].children.find(child => child.name === 'PortOfOrigin')?.children.find(child => child.name === 'Code')?.value,
                            text: structure?.children[0].children.find(child => child.name === 'PortOfOrigin')?.children.find(child => child.name === 'Name')?.value,
                        }}
                        destination={{
                            id: structure?.children[0].children.find(child => child.name === 'PortOfDestination')?.children.find(child => child.name === 'Code')?.value,
                            text: structure?.children[0].children.find(child => child.name === 'PortOfDestination')?.children.find(child => child.name === 'Name')?.value,
                        }}
                        setSource={(v) => {
                            setStructure(_struct => {
                                const s = _.cloneDeep(_struct);
                                const Shipment = s.children[0];
                                let portOfOrigin = Shipment.children.find(child => child.name === 'PortOfOrigin');
                                if (!portOfOrigin) {
                                    portOfOrigin = {
                                        name: 'PortOfOrigin',
                                        children: [
                                            { name: 'Code', value: '' },
                                            { name: 'Name', value: '' },
                                        ]
                                    }
                                    Shipment.children.push(portOfOrigin);
                                }

                                portOfOrigin.children.find(child => child.name === 'Code').value = v.id;
                                portOfOrigin.children.find(child => child.name === 'Name').value = v.text;
                                return s;
                            });
                        }}
                        setDestination={(v) => { 
                            setStructure(_struct => {
                                const s = _.cloneDeep(_struct);
                                const Shipment = s.children[0];
                                let portOfDestination = Shipment.children.find(child => child.name === 'PortOfDestination');
                                if (!portOfDestination) {
                                    portOfDestination = {
                                        name: 'PortOfDestination',
                                        children: [
                                            { name: 'Code', value: '' },
                                            { name: 'Name', value: '' },
                                        ]
                                    }
                                    Shipment.children.push(portOfDestination);
                                }

                                portOfDestination.children.find(child => child.name === 'Code').value = v.id;
                                portOfDestination.children.find(child => child.name === 'Name').value = v.text;
                                return s;
                            });
                        }}
                        seaPorts={seaPorts}
                        unlocode={unlocode}
                    />
                </Box>
                    
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', marginBottom: '12px' }}>
                    <SectionTitle sx={{ flex: 1, marginTop: 0, marginBottom: 0 }}>Creditor/Debtor</SectionTitle>
                    <Button variant='text' color='primary' onClick={() => {
                        setStructure(_struct => {
                            const s = _.cloneDeep(_struct);
                            const shipment = s.children.find(child => child.name === 'Shipment');
                            const jobCosting = shipment.children.find(child => child.name === 'JobCosting');
                            const chargeLineCollection = jobCosting.children.find(child => child.name === 'ChargeLineCollection');

                            const numNewCosts = chargeLineCollection.children.filter(child => 
                                child.children.find(child => child.name === 'Description').value.startsWith('New Cost')).length;

                            chargeLineCollection.children.unshift(_.cloneDeep(chargeLineCollection.children[0]))

                            chargeLineCollection.children[0].children.forEach(child => {
                                if (child.name === 'Description') {
                                    child.value = 'New Cost ' + (numNewCosts + 1);
                                }
                            });
                            return s;
                        });
                    }}> + Add Row</Button>
                </Box>
                <DataGrid 
                    rows={rows} 
                    columns={columns} 
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={e => console.error('[CWXml] row update failed', e)}
                    density='compact'
                    sx={{
                        fontSize: '12px',
                        '& .MuiDataGrid-columnHeaderTitle': { fontSize: '11px', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' },
                        '& .MuiDataGrid-cell': { fontSize: '12px' },
                    }}
                    onCellKeyDown={(params, event) => {
                        if (params.cellMode !== 'view') return;

                        const isPaste = (event.ctrlKey || event.metaKey) && (event.key === 'v' || event.key === 'V');
                        const isCopy = (event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'C');

                        // Paste into a highlighted cell, like a spreadsheet, without opening it. It goes
                        // through processRowUpdate like any edit: creditors match by name or code, and
                        // amounts keep the row's currency. Text that doesn't fit the column is ignored.
                        if (isPaste && PASTE_FIELDS.includes(params.field)) {
                            event.preventDefault();
                            event.stopPropagation();
                            navigator.clipboard.readText().then(text => {
                                const value = (text || '').trim();
                                if (!value) return;
                                if (params.field === 'SellOSAmountCurrency' || params.field === 'CostOSAmountCurrency') {
                                    const amount = parsePastedAmount(value);
                                    if (amount !== null) processRowUpdate({ ...params.row, [params.field]: `${amount}|` }, params.row);
                                    return;
                                }
                                processRowUpdate({ ...params.row, [params.field]: value }, params.row);
                            });
                            return;
                        }

                        // Copy a creditor/debtor as its CargoWise code, so it pastes into another row.
                        if (isCopy && (params.field === 'CreditorName' || params.field === 'DebtorName')) {
                            event.preventDefault();
                            event.stopPropagation();
                            navigator.clipboard.writeText(params.row[`${params.field === 'CreditorName' ? 'Creditor' : 'Debtor'}-Key`] || '');
                        }
                    }}
                />
                <SectionTitle>Attachments</SectionTitle>
                {_.isEmpty(products.filter(p => p.dangerous && p.dangerousMSDS)) && _.isEmpty(_(opportunity.updates).map('files').flatten().uniq().value()) && (
                    <Typography variant='body2' component={Box} sx={{ fontStyle: 'italic' }}>No attachments available</Typography>
                )}
                <Box sx={{ display: 'flex' }}>
                    {products.filter(p => p.dangerous && p.dangerousMSDS).map(product => (
                        <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '12px' }} key={product.dangerousMSDS} >
                            <a href={product.dangerousMSDS} target={'_blank'} rel={'noreferrer'} style={{ textAlign: 'center', cursor: 'pointer', textDecoration: 'none', color: '#000' }}>
                                <FontAwesomeIcon icon={fileToIcon(product.dangerousMSDS)} style={{ fontSize: '48px', marginBottom: '6px' }} />
                                <Typography variant='caption' component={Box}>
                                    {product.type} - MSDS
                                </Typography>
                            </a>
                        </Box>
                    ))}
                    {_(opportunity.updates).filter(u => !u.deleted).map('files').flatten().compact().uniq().value().map(file => (
                        <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '12px' }} key={file.url}>
                            <a href={file.url} target={'_blank'} rel={'noreferrer'} style={{ textAlign: 'center', cursor: 'pointer', textDecoration: 'none', color: '#000' }}>
                                <FontAwesomeIcon icon={fileToIcon(file.name)} style={{ fontSize: '48px', marginBottom: '6px' }} />
                                <Typography variant='caption' component={Box}>
                                    {file.name.substr(0, 20)}{file.name.length > 20 ? '...' : ''}
                                </Typography>
                            </a>
                        </Box>
                    ))}

                </Box>

                {!_.isEmpty(errors) && (
                    <>
                        <SectionTitle sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d32f2f' }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '12px' }} />
                            {errors.length === 1 ? '1 issue to fix before sending' : `${errors.length} issues to fix before sending`}
                        </SectionTitle>
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                            {errors.map((error, i) => (
                                <Box key={error} sx={{ display: 'flex', alignItems: 'stretch', gap: '10px', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                                    <Box sx={{ width: '3px', backgroundColor: '#d32f2f', flexShrink: 0 }} />
                                    <Typography variant='caption' sx={{ padding: '6px 12px 6px 0px', fontSize: '12px', lineHeight: 1.5 }}>{error}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                {!_.isEmpty(warnings) && (
                    <>
                        <SectionTitle sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#b45309' }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '12px' }} />
                            {warnings.length === 1 ? '1 warning' : `${warnings.length} warnings`} (you can still send)
                        </SectionTitle>
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                            {warnings.map((warning, i) => (
                                <Box key={warning} sx={{ display: 'flex', alignItems: 'stretch', gap: '10px', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                                    <Box sx={{ width: '3px', backgroundColor: '#f59e0b', flexShrink: 0 }} />
                                    <Typography variant='caption' sx={{ padding: '6px 12px 6px 0px', fontSize: '12px', lineHeight: 1.5 }}>{warning}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
                </>)}
                {step === 1 && (
                    <>
                        <SectionTitle>Summary</SectionTitle>
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px 16px', display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '24px', rowGap: '6px' }}>
                            {summary.map(([ label, value ]) => (
                                <Fragment key={label}>
                                    <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '12px' }}>{label}</Typography>
                                    <Typography variant='body2' sx={{ fontSize: '13px' }}>{value}</Typography>
                                </Fragment>
                            ))}
                        </Box>
                        <SectionTitle>Download</SectionTitle>
                        <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: '13px', marginBottom: '12px' }}>
                            Both files include every change you made in the previous step. The XML file is exactly what is sent to CargoWise.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <Button variant='outlined' color='primary' onClick={onDownloadCsv} disabled={rows.length === 0} startIcon={<FontAwesomeIcon icon={faFileCsv} />} sx={{ textTransform: 'none' }}>
                                Download charges table (CSV)
                            </Button>
                            <Button variant='outlined' color='primary' onClick={onDownloadXml} disabled={!structure} startIcon={<FontAwesomeIcon icon={faFileCode} />} sx={{ textTransform: 'none' }}>
                                Download XML file
                            </Button>
                        </Box>
                        {!_.isEmpty(errors) && (
                            <Box sx={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                                <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '13px', color: '#d32f2f' }} />
                                <Typography variant='body2' sx={{ fontSize: '13px', flex: 1 }}>
                                    {errors.length === 1 ? 'There is 1 issue' : `There are ${errors.length} issues`} to fix before you can send. You can still download the files.
                                </Typography>
                                <Button size='small' onClick={() => setStep(0)} sx={{ textTransform: 'none' }}>Go back and fix</Button>
                            </Box>
                        )}
                        {!_.isEmpty(warnings) && (
                            <Box sx={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #fde68a', backgroundColor: '#fffbeb', borderRadius: '6px' }}>
                                <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '13px', color: '#b45309' }} />
                                <Typography variant='body2' sx={{ fontSize: '13px', flex: 1 }}>
                                    {warnings.length === 1 ? warnings[0] : `${warnings.length} dangerous products have no class or UN code. Add them on the quote's products before sending, or CargoWise won't have the right details.`}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <DialogActions sx={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0' }}>
                {step === 0 ? (
                    <>
                        <Button onClick={onClose} color='primary'>
                            Cancel
                        </Button>
                        <Button disabled={!structure} onClick={() => setStep(1)} variant='contained' color='primary'>
                            Next
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setStep(0)} color='primary'>
                            Back
                        </Button>
                        <Button disabled={!_.isEmpty(errors)} onClick={_onOk} variant='contained' color='primary'>
                            Send to CargoWise
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}

// Edit cells report every change to the grid as it happens (setEditCellValue), so the value is
// there when the grid ends the edit, whatever ended it; processRowUpdate then saves it.
// cellToFocusAfter: 'below' after Enter (like the grid's own text cells), 'none' after a mouse pick.
function stopEditing(params, cellToFocusAfter = 'none') {
    if (params.api.getCellMode(params.id, params.field) === 'edit') {
        params.api.stopCellEditMode({ id: params.id, field: params.field, cellToFocusAfter });
    }
}

// Field and Charge Code. Enter, Tab and Escape are the grid's own. The up and down arrows save and
// open the same cell in the row above or below, so a column of codes can be typed or pasted one
// after another.
function TextEditCell({ params, rowCount }) {
    return (
        <TextField
            fullWidth
            autoFocus
            defaultValue={params.value}
            onFocus={event => event.target.select()}
            onChange={e => params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value })}
            onKeyDown={e => {
                if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                e.preventDefault();
                e.stopPropagation();
                const next = params.id + (e.key === 'ArrowDown' ? 1 : -1);
                stopEditing(params);
                if (next < 0 || next >= rowCount) return;
                setTimeout(() => {
                    params.api.setCellFocus(next, params.field);
                    params.api.startCellEditMode({ id: next, field: params.field });
                }, 0);
            }}
        />
    );
}

function CurrencyEditCell({ params, organization }) {
    // Typing on a highlighted cell or pressing Delete opens it with an empty value (no currency);
    // the currency then comes from the row's own currency-code field, which isn't being edited.
    const rowCurrency = params.row[params.field === 'SellOSAmountCurrency' ? 'SellOSCurrency-Code' : 'CostOSCurrency-Code'];
    const [ initialValue = '', initialCurrency ] = (params.value || '').split('|');
    const [ value, setValue ] = useState(initialValue);
    const [ currency, setCurrency ] = useState(initialCurrency || rowCurrency);
    const report = (v, c) => params.api.setEditCellValue({ id: params.id, field: params.field, value: `${v}|${c || ''}` });

    return (
        // CurrencyTextField handles Enter itself and stops it reaching the grid, so catch it first
        // and end the edit moving down a row, as the grid's text cells do.
        <Box
            sx={{ width: '100%' }}
            onKeyDownCapture={e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    stopEditing(params, 'below');
                }
            }}
        >
        <CurrencyTextField
            value={value}
            setValue={v => { setValue(v); report(v, currency); }}
            currency={currency}
            setCurrency={c => { setCurrency(c); report(value, c); }}
            organization={organization}
            forceTextField={true}
            focusedId={'custom'}
            focused={'custom'}
            // Put the cursor in the amount when the cell opens, so typing or pasting goes straight in.
            inputRef={input => {
                if (input && !input._focused) {
                    input.focus();
                    input.select();
                    input._focused = true;
                }
            }}
            onBlur={() => stopEditing(params)}
        />
        </Box>
    );
}

// Creditor/Debtor: pick from the list with the mouse, arrows + Enter, or type/paste a name or a
// CargoWise code and leave the cell.
function CreditorEditCell({ params, options }) {
    const [ initial ] = useState(params.value || null);
    // Enter picks the highlighted creditor only once something was typed; otherwise it would take
    // the first name in the whole list.
    const typed = useRef(false);
    return (
        <Autocomplete
            fullWidth
            autoHighlight
            openOnFocus
            defaultValue={initial}
            options={options}
            onChange={(event, newValue) => {
                const byKeyboard = event?.type === 'keydown';
                if (!newValue || (byKeyboard && !typed.current)) {
                    stopEditing(params, byKeyboard ? 'below' : 'none');
                    return;
                }
                params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
                stopEditing(params, byKeyboard ? 'below' : 'none');
            }}
            onInputChange={(event, text, reason) => {
                if (reason !== 'input') return;
                typed.current = text.trim().length > 0;
                params.api.setEditCellValue({ id: params.id, field: params.field, value: text });
            }}
            renderInput={(inputParams) => (
                <TextField
                    {...inputParams}
                    // Focus at once: letters typed on a highlighted cell start the edit and must land here.
                    inputRef={input => {
                        if (input && !input._focused) {
                            input.focus();
                            input._focused = true;
                        }
                    }}
                    onFocus={event => { event.target.select(); }}
                    fullWidth
                />
            )}
        />
    );
}

function fileToIcon(filename) {
    return {
        'jpg': faFileJpg,
        'jpeg': faFileJpg,
        'pdf': faFilePdf,
        'doc': faFileWord,
        'docx': faFileWord,
        'xls': faFileExcel,
        'xlsx': faFileExcel,
        'ppt': faFilePowerpoint,
        'pptx': faFilePowerpoint,
    }[filename.split('.').pop().toLowerCase()] || faFile;
}
